// components/PolyShiftGroupsManager.jsx
import React, { useState, useCallback, useMemo, useEffect } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Button,
  Row,
  Col,
  Input,
  Badge,
  Alert,
} from "reactstrap";

// Reusable File Item Component
const FileItem = React.memo(
  ({
    file,
    fileId,
    isDragging,
    onDragStart,
    onDragEnd,
    onRemove,
    timepointIndex,
    condType,
  }) => (
    <div
      key={fileId}
      draggable={true}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      style={{
        padding: "2px 12px",
        backgroundColor: isDragging
          ? "rgba(255,255,255,0.5)"
          : "rgba(255,255,255,0.8)",
        borderRadius: "6px",
        fontSize: "11px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        border: "1px solid rgba(255,255,255,0.5)",
        cursor: "grab",
        transition: "all 0.2s ease",
        opacity: isDragging ? 0.5 : 1,
        transform: isDragging ? "scale(0.95)" : "scale(1)",
        marginBottom: "4px",
      }}
      onMouseEnter={(e) => {
        if (!isDragging) {
          e.currentTarget.style.cursor = "grabbing";
          e.currentTarget.style.transform = "translateY(-1px)";
          e.currentTarget.style.boxShadow = "0 2px 6px rgba(0, 0, 0, 0.1)";
        }
      }}
      onMouseLeave={(e) => {
        if (!isDragging) {
          e.currentTarget.style.cursor = "grab";
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "none";
        }
      }}
    >
      <span
        style={{
          overflow: "hidden",
          color: "#374151",
          fontWeight: "500",
          flex: 1,
          marginRight: "8px",
        }}
        title={file?.file_name || fileId}
      >
        {file?.file_name || fileId}
      </span>
      <Button
        size="sm"
        onClick={(e) => {
          e.stopPropagation();
          onRemove(timepointIndex, condType, [fileId]);
        }}
        style={{
          backgroundColor: "transparent",
          border: "none",
          color: "#dc3545",
          padding: "2px 4px",
          fontSize: "10px",
          minWidth: "20px",
          height: "20px",
        }}
      >
        <i className="ri-close-line"></i>
      </Button>
    </div>
  )
);

// Reusable Timepoint Card Component
const TimepointCard = React.memo(
  ({
    condType,
    files,
    timepointIndex,
    fractionName,
    dragOverTarget,
    onDragEnter,
    onDragOver,
    onDragLeave,
    onDrop,
    onClearTimepoint,
    renderFileItem,
    getBorderColor,
    getBackgroundColor,
  }) => {
    const isActive = dragOverTarget === `${timepointIndex}-${condType}`;

    return (
      <Card
        style={{
          backgroundColor: getBackgroundColor(condType),
          border: isActive
            ? `2px dashed ${getBorderColor(condType)}`
            : `1px solid ${getBorderColor(condType)}`,
          borderRadius: "8px",
          transition: "all 0.2s ease",
          transform: isActive ? "scale(1.02)" : "scale(1)",
          boxShadow: isActive
            ? `0 4px 12px rgba(0,0,0,0.15), 0 0 0 4px ${getBorderColor(
                condType
              )}20`
            : "none",
          marginBottom: "16px",
        }}
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        <CardBody style={{ padding: "20px" }}>
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="d-flex align-items-center">
              <div
                style={{
                  width: "16px",
                  height: "16px",
                  borderRadius: "50%",
                  backgroundColor: getBorderColor(condType),
                  marginRight: "8px",
                }}
              />
              <span
                style={{
                  fontWeight: "600",
                  fontSize: "14px",
                  color: getBorderColor(condType),
                }}
              >
                {fractionName}
              </span>
            </div>
            {/* Clear Group Button */}
            <Button
              size="sm"
              onClick={() => onClearTimepoint?.(timepointIndex, condType)}
              style={{
                backgroundColor: "transparent",
                border: "1px solid #f59e0b",
                color: "#f59e0b",
                fontSize: "10px",
                padding: "4px 8px",
                fontWeight: "500",
                borderRadius: "4px",
                marginTop: "8px",
              }}
            >
              <i className="ri-delete-bin-line me-1"></i>
              Clear
            </Button>
          </div>

          {/* Files or Drop Zone */}
          {files.length === 0 ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "100px",
                border: "2px dashed #ccc",
                borderRadius: "8px",
                backgroundColor: "rgba(255,255,255,0.5)",
                color: "#999",
                fontSize: "12px",
                textAlign: "center",
              }}
            >
              <i
                className="ri-upload-cloud-2-line"
                style={{
                  fontSize: "24px",
                  marginBottom: "8px",
                }}
              />
              <div>Drag files here</div>
              <div style={{ fontSize: "10px", marginTop: "4px" }}>
                Drop fraction files into this group
              </div>
            </div>
          ) : (
            <div>
              <div
                style={{
                  minHeight: "100px",
                  overflowY: "auto",
                  paddingRight: "4px",
                }}
              >
                {files.map((fileId) =>
                  renderFileItem(fileId, condType, timepointIndex)
                )}
              </div>

              {/* Clear Group Button */}
            </div>
          )}
        </CardBody>
      </Card>
    );
  }
);

const PolyShiftGroupsManager = ({
  files = [],
  allFiles = [],
  timepoints = [],
  settings = {},
  onUpdateSetting,
  onAddTimepoint,
  onRemoveTimepoint,
  onUpdateTimepointName,
  onAddFilesToTimepoint,
  onRemoveFilesFromTimepoint,
  onClearTimepoint,
  onClearAll,
  currentMaxVisible = 0,
  getTimepointStats,
  maxTimepoints = 12,
}) => {
  const [draggedFiles, setDraggedFiles] = useState([]);
  const [draggedFileSourceGroup, setDraggedFileSourceGroup] = useState(null);
  const [dragOverTarget, setDragOverTarget] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [lastSelectedIndex, setLastSelectedIndex] = useState(-1);

  // Create displayGroups used for the quick assign bar buttons
  const groupConfigs = [
    { condType: "control", color: "#E8F5E8" },
    { condType: "treat",   color: "#FDEDEE" },
  ];

  const displayGroups = Array.from({ length: maxTimepoints + 1 }).flatMap((_, i) => 
    groupConfigs.map(config => ({ id: `${config.condType}-${i}`,
                                  timepointIndex: i, 
                                  ...config }))
  );
 
  // Get available files that haven't been assigned
  const getAvailableFiles = useMemo(() => {
    return files.filter(
      (file) =>
        !timepoints.some(
          (tp) =>
            tp.controlFiles.includes(file.file_id) ||
            tp.treatFiles.includes(file.file_id)
        )
    );
  }, [files, timepoints]);

  const minCellWidth = useMemo(() => {
    if (getAvailableFiles.length === 0) return 180;
      const longestName = Math.max(...getAvailableFiles.map(f => f.file_name.length));
      const calculated = longestName * 7 + 40;
    return Math.min(Math.max(calculated, 180), 280);
  }, [getAvailableFiles]);
    useEffect(() => {
      if (Array.isArray(allFiles) && allFiles.length > 0) {
        window.globalFileCache = allFiles;
      }
  }, [allFiles]);



  const getFileById = useCallback(
    (fileId) => {
      return allFiles.find((f) => f.file_id === fileId);
    },
    [allFiles]
  );

  // Color functions - using EIRNA green and red
  const getBackgroundColor = useCallback((condType) => {
    return condType === "control" ? "#E8F5E8" : "#FDEDEE"; // Green for control, Red for treatment
  }, []);

  const getBorderColor = useCallback((condType) => {
    return condType === "control" ? "#1a6d51" : "#dc3545"; // EIRNA green for control, Red for treatment
  }, []);

  // Mouse interaction handlers for better UX
  const handleFileMouseEnter = useCallback((e, isAssigned, isSelected) => {
    if (!isAssigned && !isSelected) {
      e.currentTarget.style.backgroundColor = "#f9fafb";
      e.currentTarget.style.borderColor = "#d1d5db";
    }
  }, []);

  const handleFileMouseLeave = useCallback((e, isAssigned, isSelected) => {
    if (!isAssigned && !isSelected) {
      e.currentTarget.style.backgroundColor = "white";
      e.currentTarget.style.borderColor = "#e5e7eb";
    }
  }, []);

  // Enhanced drag handlers
  const handleDragStart = useCallback(
    (e, fileId) => {
      try {
        const filesToDrag = selectedFiles.includes(fileId)
          ? selectedFiles
          : [fileId];
        setDraggedFiles(filesToDrag);
        setDraggedFileSourceGroup(null);

        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", fileId);
        e.dataTransfer.setData("application/json", JSON.stringify(filesToDrag));

        document.body.style.cursor = "grabbing";
      } catch (error) {
        console.error("Drag start error:", error);
      }
    },
    [selectedFiles]
  );

  const handleGroupFileDragStart = useCallback(
    (e, fileId, sourceTimepointIndex, sourceCondType) => {
      e.stopPropagation();
      setDraggedFiles([fileId]);
      setDraggedFileSourceGroup({
        timepointIndex: sourceTimepointIndex,
        condType: sourceCondType,
      });
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", fileId);
      e.dataTransfer.setData("application/json", JSON.stringify([fileId]));
      document.body.style.cursor = "grabbing";
    },
    []
  );

  const handleDragEnter = useCallback((e, timepointIndex, condType) => {
    e.preventDefault();
    setDragOverTarget(`${timepointIndex}-${condType}`);
  }, []);

  const handleDragOver = useCallback((e, timepointIndex, condType) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverTarget(`${timepointIndex}-${condType}`);
  }, []);

  const handleDragLeave = useCallback((e) => {
    if (!e.relatedTarget || !e.currentTarget.contains(e.relatedTarget)) {
      setDragOverTarget(null);
    }
  }, []);

  const handleDrop = useCallback(
    (e, timepointIndex, condType) => {
      e.preventDefault();
      e.stopPropagation();

      let filesToAdd = [];

      try {
        const jsonData = e.dataTransfer.getData("application/json");
        if (jsonData) {
          filesToAdd = JSON.parse(jsonData);
        } else {
          const singleFile = e.dataTransfer.getData("text/plain");
          if (singleFile) {
            filesToAdd = [singleFile];
          }
        }
      } catch (error) {
        if (draggedFiles.length > 0) {
          filesToAdd = draggedFiles;
        }
      }

      if (filesToAdd.length > 0) {
        if (draggedFileSourceGroup) {
          const {
            timepointIndex: sourceTimepointIndex,
            condType: sourceCondType,
          } = draggedFileSourceGroup;

          const isDifferentLocation =
            sourceTimepointIndex !== timepointIndex ||
            sourceCondType !== condType;

          if (isDifferentLocation) {
            console.log("Moving files from:", draggedFileSourceGroup, "to:", {
              timepointIndex,
              condType,
            });
            onRemoveFilesFromTimepoint(
              sourceTimepointIndex,
              sourceCondType,
              filesToAdd
            );
            onAddFilesToTimepoint(timepointIndex, condType, filesToAdd);
          }
        } else {
          console.log("Adding files from selection area to:", {
            timepointIndex,
            condType,
          });
          onAddFilesToTimepoint(timepointIndex, condType, filesToAdd);
          setSelectedFiles([]);
        }

        setDraggedFiles([]);
        setDraggedFileSourceGroup(null);
      }

      setDragOverTarget(null);
      document.body.style.cursor = "default";
    },
    [
      draggedFiles,
      draggedFileSourceGroup,
      onAddFilesToTimepoint,
      onRemoveFilesFromTimepoint,
    ]
  );

  const handleDragEnd = useCallback(() => {
    setDragOverTarget(null);
    setDraggedFiles([]);
    setDraggedFileSourceGroup(null);
    document.body.style.cursor = "default";
  }, []);

  const handleFileClick = useCallback(
    (e, fileId, fileIndex) => {
      e.preventDefault();

      const isAssigned = timepoints.some(
        (tp) =>
          tp.controlFiles.includes(fileId) || tp.treatFiles.includes(fileId)
      );
      if (isAssigned) return;

      if (e.shiftKey && lastSelectedIndex !== -1) {
        const startIndex = Math.min(lastSelectedIndex, fileIndex);
        const endIndex = Math.max(lastSelectedIndex, fileIndex);
        const rangeFileIds = getAvailableFiles
          .slice(startIndex, endIndex + 1)
          .map((file) => file.file_id);

        const allInRangeSelected = rangeFileIds.every((id) =>
          selectedFiles.includes(id)
        );

        setSelectedFiles((prev) => {
          if (allInRangeSelected) {
            return prev.filter((id) => !rangeFileIds.includes(id));
          } else {
            const newFiles = rangeFileIds.filter((id) => !prev.includes(id));
            return [...prev, ...newFiles];
          }
        });
      } else {
        setSelectedFiles((prev) =>
          prev.includes(fileId)
            ? prev.filter((id) => id !== fileId)
            : [...prev, fileId]
        );
        setLastSelectedIndex(fileIndex);
      }
    },
    [timepoints, lastSelectedIndex, getAvailableFiles, selectedFiles]
  );

  const shouldShowTreatment = useCallback(() => true, []);

  const handleClearTimepointSection = useCallback(
    (timepointIndex, condType) => {
      console.log(
        `Clearing section: timepoint=${timepointIndex}, condType=${condType}`
      );
      onClearTimepoint(timepointIndex, condType);
    },
    [onClearTimepoint]
  );

  const renderFileItem = useCallback(
    (fileId, condType, timepointIndex) => {
      const file = getFileById(fileId);
      const isDragging = draggedFiles.includes(fileId);

      return (
        <FileItem
          key={fileId}
          file={file}
          fileId={fileId}
          isDragging={isDragging}
          onDragStart={(e) =>
            handleGroupFileDragStart(e, fileId, timepointIndex, condType)
          }
          onDragEnd={handleDragEnd}
          onRemove={onRemoveFilesFromTimepoint}
          timepointIndex={timepointIndex}
          condType={condType}
        />
      );
    },
    [
      getFileById,
      onRemoveFilesFromTimepoint,
      draggedFiles,
      handleGroupFileDragStart,
      handleDragEnd,
    ]
  );

  const availableFilesCount = getAvailableFiles.length;

  return (
    <Card
      className="mb-4"
      style={{
        border: "1px solid #e5e7eb",
        borderRadius: "1px",
        height: "100%",
        minHeight: "400px",
      }}
    >
      <CardHeader>
        <div className="d-flex justify-content-between align-items-center mb-1">
          <div>
            <h5
              className="mb-0"
              style={{ color: "#1a6d51", fontSize: "18px", fontWeight: "600" }}
            >
              <i className="ri-equalizer-line me-2"></i>
              Polysome Fraction File Assignment
            </h5>
          </div>

          <div className="d-flex gap-2">
            <Button
              size="sm"
              onClick={onClearAll}
              style={{
                backgroundColor: "transparent",
                border: "1px solid #dc3545",
                color: "#dc3545",
                fontSize: "12px",
                padding: "6px 12px",
                borderRadius: "6px",
              }}
            >
              <i className="ri-delete-bin-line me-1"></i>
              Clear All
            </Button>
            <Button
              size="sm"
              onClick={onAddTimepoint}
              disabled={currentMaxVisible >= maxTimepoints}
              style={{
                backgroundColor: "#28a745",
                borderColor: "#28a745",
                color: "white",
                fontSize: "12px",
                padding: "6px 12px",
                borderRadius: "6px",
              }}
            >
              <i className="ri-add-line me-1"></i>
              Add Fraction
            </Button>
            <Button
              size="sm"
              onClick={onRemoveTimepoint}
              disabled={currentMaxVisible <= 0}
              style={{
                backgroundColor: "#28a745",
                borderColor: "#28a745",
                color: "white",
                fontSize: "12px",
                padding: "6px 12px",
                borderRadius: "6px",
              }}
            >
              <i className="ri-subtract-line me-1"></i>
              Remove Fraction
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardBody style={{ padding: "20px" }}>
        {/* Available Files Section */}
        <div className="mb-4">
          {availableFilesCount > 0 && (
            <div
              className="text-center mb-2"
              style={{
                background:
                  "linear-gradient(135deg, #1a6d51 0%, #29a161ff 100%)",
                color: "white",
                borderRadius: "6px",
                padding: "4px 12px",
                fontSize: "11px",
                display: "inline-block",
              }}
            >
              <i
                className="ri-cursor-line me-1"
                style={{ fontSize: "12px" }}
              ></i>
              Click files to select/deselect them. Use{" "}
              <kbd
                style={{
                  fontSize: "9px",
                  padding: "1px 3px",
                  backgroundColor: "rgba(255,255,255,0.2)",
                  border: "none",
                }}
              >
                Shift
              </kbd>
              +Click for range selection. Drag files to groups.
            </div>
          )}

          <div
            style={{
              overflowY: "auto",
              border: "1px solid #e5e7eb",
              borderRadius: "6px",
              padding: "8px",
              backgroundColor: "#fafafa",
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: `repeat(auto-fill, minmax(${minCellWidth}px, 1fr))`,
                gap: "6px",
              }}
            >
              {getAvailableFiles.map((file, index) => {
                const isSelected = selectedFiles.includes(file.file_id);
                const isAssigned = timepoints.some(
                  (tp) =>
                    tp.controlFiles.includes(file.file_id) ||
                    tp.treatFiles.includes(file.file_id)
                );

                return (
                  <div
                    key={file.file_id}
                    draggable={!isAssigned}
                    onDragStart={(e) =>
                      !isAssigned && handleDragStart(e, file.file_id)
                    }
                    onDragEnd={handleDragEnd}
                    onClick={(e) => handleFileClick(e, file.file_id, index)}
                    onMouseEnter={(e) =>
                      handleFileMouseEnter(e, isAssigned, isSelected)
                    }
                    onMouseLeave={(e) =>
                      handleFileMouseLeave(e, isAssigned, isSelected)
                    }
                    style={{
                      padding: "6px",
                      border: isSelected
                        ? "1px solid #2F9E37"
                        : "1px solid #e5e7eb",
                      borderRadius: "4px",
                      backgroundColor: isAssigned
                        ? "#f3f4f6"
                        : isSelected
                        ? "#f0fdf4"
                        : "white",
                      cursor: isAssigned ? "not-allowed" : "pointer",
                      fontSize: "12px",
                      opacity: isAssigned ? 0.7 : 1,
                      transition: "all 0.2s ease",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      userSelect: "none",
                    }}
                  >
                    <Input
                      type="checkbox"
                      checked={isSelected && !isAssigned}
                      readOnly
                      style={{
                        pointerEvents: "none",
                        margin: 0,
                        width: "14px",
                        height: "14px",
                      }}
                    />
                    <span
                      style={{
                        fontSize: "11px",
                        lineHeight: "1.2",
                        overflow: "hidden",
                        flex: 1,
                        color: isAssigned ? "#9ca3af" : "#374151",
                      }}
                      title={file.file_name}
                    >
                      {file.file_name}
                    </span>
                    {isAssigned && (
                      <Badge
                        style={{
                          fontSize: "8px",
                          backgroundColor: "#9ca3af",
                          padding: "2px 4px",
                        }}
                      >
                        Used
                      </Badge>
                    )}
                  </div>
                );
              })}
              {availableFilesCount === 0 && (
                <div
                  className="text-muted text-center"
                  style={{
                    gridColumn: "1 / -1",
                    padding: "20px",
                    fontSize: "14px",
                  }}
                >
                  All files have been assigned to fractions
                </div>
              )}
            </div>
          </div>
        </div>


        {/* Quick Assign Bar */} 
        {selectedFiles.length > 0 && (
          <div className="mb-4 p-3 bg-light rounded">
            <div className="d-flex align-items-center justify-content-between">
              <span className="fw-bold">
                Assign {selectedFiles.length} selected file(s) to:
              </span>
              <div className="row g-2" style={{ maxWidth: "400px"}}>
                {displayGroups
                  .filter((group) => {
                    // timepoint must be visible
                    const isVisibleFraction = group.timepointIndex <= currentMaxVisible;
                    return isVisibleFraction;
                  })
                  .map((group) => (
                    <div className={"col-6"} key={group.id}>
                      <Button
                        size="sm"
                        style={{
                          backgroundColor: group.color,
                          borderColor: group.color,
                          color: "black",
                        }}
                        onClick={() => {
                          onAddFilesToTimepoint(group.timepointIndex, group.condType, selectedFiles);
                          setSelectedFiles([]);
                        }}
                      >
                        {`${group.condType} Fraction ${group.timepointIndex + 1}`}
                      </Button>
                    </div>
                  ))
                }
              </div>
            </div>
          </div>
        )}

        {/* Control/Treatment Name Inputs */}
        <Row className="mb-3">
          <Col md={shouldShowTreatment() ? 6 : 12}>
            <Input
              type="text"
              value={settings.customControlName || "Control"}
              onChange={(e) =>
                onUpdateSetting?.("customControlName", e.target.value)
              }
              placeholder="Control"
              style={{
                fontSize: "14px",
                textAlign: "center",
                fontWeight: "600",
                borderRadius: "8px",
                backgroundColor: "#E8F5E8", // EIRNA green background
                border: "2px solid #1a6d51", // EIRNA green border
                color: "#1a6d51", // EIRNA green text
              }}
            />
          </Col>
          {shouldShowTreatment() && (
            <Col md={6}>
              <Input
                type="text"
                value={settings.customTreatmentName || "Treatment"}
                onChange={(e) =>
                  onUpdateSetting?.("customTreatmentName", e.target.value)
                }
                placeholder="Treatment"
                style={{
                  fontSize: "14px",
                  textAlign: "center",
                  fontWeight: "600",
                  borderRadius: "8px",
                  backgroundColor: "#FDEDEE", // Red background
                  border: "2px solid #dc3545", // Red border
                  color: "#dc3545", // Red text
                }}
              />
            </Col>
          )}
        </Row>

        {/* Fraction Grid */}
        {timepoints
          .slice(0, currentMaxVisible + 1)
          .map((timepoint, timepointIndex) => (
            <div
              key={timepointIndex}
              className="mb-4"
              data-timepoint={timepointIndex}
            >
              {/* Fraction Name Input - Similar to comparison profile */}
              <div className="mb-1">
                <Input
                  type="text"
                  value={timepoint.groupName}
                  onChange={(e) =>
                    onUpdateTimepointName(timepointIndex, e.target.value)
                  }
                  placeholder={`Fraction ${timepointIndex + 1}`}
                  style={{
                    fontSize: "16px",
                    textAlign: "center",
                    fontWeight: "600",
                    borderRadius: "8px",
                    backgroundColor: "#f8f9fa",
                    border: "2px solid #6c757d",
                    color: "#495057",
                    padding: "3px 16px",
                  }}
                />
              </div>

              <Row>
                {/* Control Column */}
                <Col md={shouldShowTreatment() ? 6 : 12}>
                  <TimepointCard
                    condType="control"
                    files={timepoint.controlFiles}
                    timepointIndex={timepointIndex}
                    fractionName={
                      timepoint.groupName || `Fraction ${timepointIndex + 1}`
                    }
                    dragOverTarget={dragOverTarget}
                    onDragEnter={(e) =>
                      handleDragEnter(e, timepointIndex, "control")
                    }
                    onDragOver={(e) =>
                      handleDragOver(e, timepointIndex, "control")
                    }
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, timepointIndex, "control")}
                    onClearTimepoint={handleClearTimepointSection}
                    renderFileItem={renderFileItem}
                    getBorderColor={getBorderColor}
                    getBackgroundColor={getBackgroundColor}
                  />
                </Col>

                {/* Treatment Column */}
                {shouldShowTreatment() && (
                  <Col md={6}>
                    <TimepointCard
                      condType="treat"
                      files={timepoint.treatFiles}
                      timepointIndex={timepointIndex}
                      fractionName={
                        timepoint.groupName || `Fraction ${timepointIndex + 1}`
                      }
                      dragOverTarget={dragOverTarget}
                      onDragEnter={(e) =>
                        handleDragEnter(e, timepointIndex, "treat")
                      }
                      onDragOver={(e) =>
                        handleDragOver(e, timepointIndex, "treat")
                      }
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, timepointIndex, "treat")}
                      onClearTimepoint={handleClearTimepointSection}
                      renderFileItem={renderFileItem}
                      getBorderColor={getBorderColor}
                      getBackgroundColor={getBackgroundColor}
                    />
                  </Col>
                )}
              </Row>
            </div>
          ))}

        {timepoints.length === 0 && (
          <div className="text-center py-4">
            <div className="text-muted">
              <i
                className="ri-equalizer-line"
                style={{
                  fontSize: "48px",
                  display: "block",
                  marginBottom: "16px",
                }}
              ></i>
              <h6>No fractions added yet</h6>
              <p>Click "Add Fraction" to get started</p>
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default PolyShiftGroupsManager;
